import { useCallback } from 'react';
import { noop } from 'lodash';
import type { GetImageUploadQueryResult } from '@frameio/graphql-client/generated';
import { useGetImageUploadLazyQuery } from '@frameio/graphql-client/generated';
import { useLoadingToast } from '@frameio/shared/resources';
import { imageUploadClient } from '@frameio/shared/utils';
import {
  type CreateWorkspaceMutationResult,
  type CreateWorkspaceMutationVariables,
  useCreateWorkspaceMutation,
} from '../gql/generated/CreateWorkspace';
import TEXT from './generated/TEXT/useCreateWorkspace';

export type CreateSuccessCallback = (
  workspace: NonNullable<
    NonNullable<
      NonNullable<CreateWorkspaceMutationResult['data']>['createWorkspace']
    >['workspace']
  >
) => void;
type CreateWorkspaceOpts = {
  onError?: () => void;
  onSuccess?: CreateSuccessCallback;
};
type Details = Omit<CreateWorkspaceMutationVariables['input'], 'image'>;
type CreateWorkspaceReturn = (details: Details, blob?: Blob) => void;
const DEFAULT_OPTS: CreateWorkspaceOpts = {
  onError: noop,
  onSuccess: noop,
};
/**
 * The useCreateWorkspace hook can be used to create a workspace. Adding an avatar
 * image requires providing an imageUploadUrl and a blob for the avatar.
 *
 * @param opts.onError an optional callback fired if the create fails
 * @param opts.onSuccess an optional callback fired if the create succeeds
 * @returns a function to call when ready to create the workspace
 */
export function useCreateWorkspace(
  opts?: CreateWorkspaceOpts
): CreateWorkspaceReturn {
  const { onError, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [createWorkspace] = useCreateWorkspaceMutation();
  const [imageUploadQuery] = useGetImageUploadLazyQuery({
    /**
     * Avoid caching the `imageUpload` data, as a unique id is required for
     * each subsequent upload.
     */
    fetchPolicy: 'network-only',
  });

  const { addToast, updateAndHideToast } = useLoadingToast();
  const { loading, errorMessage, successMessage } = TEXT;

  return useCallback(
    /**
     * @param details the CreateWorkspaceInput values without the image (accountId
     *                and name)
     * @param blob optional Blob instance for the avatar
     */
    async (details, blob) => {
      const variables: CreateWorkspaceMutationVariables = {
        input: {
          ...details,
        },
      };
      let imageUploadResult: GetImageUploadQueryResult;
      let imageUploadUrl: string;
      const toastId = addToast(loading);
      if (blob) {
        imageUploadResult = await imageUploadQuery({
          variables: {
            mimeType: blob.type,
            resourceType: 'WORKSPACE',
          },
        });
        if (imageUploadResult?.data?.imageUpload?.url) {
          imageUploadUrl = imageUploadResult.data.imageUpload.url;
          variables.input.image = {
            id: imageUploadResult.data.imageUpload.id,
            type: blob.type,
          };
          // Kick off async upload to s3
          imageUploadClient(imageUploadUrl, blob);
        }
      }
      const handleError = () => {
        updateAndHideToast(toastId, {
          title: errorMessage,
          variant: 'error',
        });
        onError?.();
      };
      try {
        const response = await createWorkspace({
          variables,
          refetchQueries: [
            'GetWorkspacesForNavigation',
            'GetWorkspacesBasic',
            'GetWorkspacesForDataTable',
          ],
        });
        if (response.data?.createWorkspace?.workspace) {
          updateAndHideToast(toastId, {
            title: successMessage,
            variant: 'success',
          });
          onSuccess?.(response.data.createWorkspace.workspace);
        } else {
          handleError();
        }
      } catch (e) {
        handleError();
      }
    },
    [
      createWorkspace,
      imageUploadQuery,
      onError,
      onSuccess,
      addToast,
      updateAndHideToast,
      loading,
      errorMessage,
      successMessage,
    ]
  );
}

TEXT.DEFINE({
  loading: 'Creating Workspace',
  errorMessage: "Couldn't create Workspace",
  successMessage: 'Workspace created',
});
